import {omit} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {TeamModel} from '../../../models/api/teamModel';
import {TeamAvatar} from '../../common/avatars/teamAvatar';
import {InfoTipContainer} from '../../common/infoTips/infoTipContainer';
import {NavigationLineProps} from '../../common/navigation/navigationLine';
import {
  NavigationLineChevron,
  NavigationLineChevronProps,
} from '../../common/navigation/navigationLineChevron';
import {NavigationLineIcon} from '../../common/navigation/navigationLineIcon';
import {NavigationLineLabel} from '../../common/navigation/navigationLineLabel';
import {WorkspaceLineDropTarget} from './line/workspaceLineDropTarget';
import {WorkspaceLineMoreProps} from './line/workspaceLineMore';
import {WorkspaceLineMoreContainer} from './line/workspaceLineMoreContainer';
import {WorkspaceItemRefinedResultsIndicator} from './workspaceItemRefinedResultsIndicator';

/*
 * Props.
 */

export interface WorkspaceTeamItemProps
  extends NavigationLineProps,
    NavigationLineChevronProps,
    WorkspaceLineMoreProps {
  /** The team to display. */
  team: TeamModel;
  /** Should the team name be bold */
  isUnread?: boolean;
  /** Should the "more" button be rendered */
  renderMenu?: RendererOf<PopoverTransferProps>;
  shouldShowDemoInboxTip?: boolean;
  shouldAlphabetizeWorkspace: boolean;
  shouldShowRefinedResultsIndicator: boolean;
  hasP2Refinements: boolean;
}

/*
 * Style.
 */

const StyledDemoInboxTipDiv = styled.div`
  position: absolute;
  left: 95px;
  top: 1px;
`;

const StyledLabelWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const StyledWorkspaceItemRefinedResultsIndicator = styled(WorkspaceItemRefinedResultsIndicator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

/** Display a team as a P1 workspace line. */
export const WorkspaceTeamItem: FC<WorkspaceTeamItemProps> = (props) => (
  <WorkspaceLineDropTarget {...props} renderTooltip={() => props.team.name} workspaceDragItem={undefined}>
    <NavigationLineChevron {...props} />
    <NavigationLineIcon>
      <TeamAvatar team={props.team} size={VisualSizesEnum.SMALL} />
    </NavigationLineIcon>
    <NavigationLineLabel
      isUnread={props.isUnread}
      isTitle
      isEditingWorkspace={props.isEditingWorkspace}
      shouldAllowOverflow={props.shouldShowDemoInboxTip} // Necessary for the demo tip pulse effect.
    >
      {props.hasP2Refinements ? (
        <StyledLabelWrapperDiv>
          <Trans id="workspace-team-inboxes">Team inboxes</Trans>
          {props.shouldShowRefinedResultsIndicator && <StyledWorkspaceItemRefinedResultsIndicator />}
          {props.shouldShowDemoInboxTip && renderDemoInboxTip()}
        </StyledLabelWrapperDiv>
      ) : (
        <>
          <Trans id="workspace-team-inboxes">Team inboxes</Trans>
          {props.shouldShowDemoInboxTip && renderDemoInboxTip()}
        </>
      )}
    </NavigationLineLabel>

    <WorkspaceLineMoreContainer
      // Omit isEditingWorkspaces to avoid showing a checkbox in the team inbox header
      {...omit(props, 'isEditingWorkspace')}
    />
  </WorkspaceLineDropTarget>
);

/*
 * Helpers.
 */

function renderDemoInboxTip() {
  return (
    <StyledDemoInboxTipDiv>
      <InfoTipContainer
        tipType={OnboardingTipTypesEnum.DEMO_INBOX_TEAM_WORKSPACE}
        title={<Trans id="infotip-workspaceteamitem-title">Centralize all your communication in Front</Trans>}
        description={
          <Trans id="infotip-workspaceteamitem-description">
            Connect chat, social media, and email in one place.
          </Trans>
        }
        placement="top"
      />
    </StyledDemoInboxTipDiv>
  );
}
